import { useToast as useToastBase } from 'vue-toast-notification-original';
import { copyToClipboard } from '@/utils/clipboard';

let toastIdCounter = 0;
const dismissRegistry = new Map();
let clickHandlerInstalled = false;

const COPY_ICON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>';

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(str) {
  return escapeHtml(str);
}

function wrapToastMessage(message, toastId) {
  const safeMessage = escapeHtml(message);
  return (
    `<div class="app-toast" data-toast-id="${toastId}" data-toast-message="${escapeAttr(message)}">`
    + '<button type="button" class="app-toast__close" aria-label="Close toast">&times;</button>'
    + `<span class="app-toast__message">${safeMessage}</span>`
    + '<button type="button" class="app-toast__copy" aria-label="Copy message to clipboard" title="Copy to clipboard">'
    + `${COPY_ICON_SVG}</button>`
    + '</div>'
  );
}

function installClickHandler() {
  if (clickHandlerInstalled) return;
  clickHandlerInstalled = true;

  document.body.addEventListener('click', (event) => {
    const closeBtn = event.target.closest('.app-toast__close');
    if (closeBtn) {
      event.preventDefault();
      event.stopPropagation();
      const toastId = closeBtn.closest('.app-toast')?.dataset?.toastId;
      const dismiss = toastId != null ? dismissRegistry.get(toastId) : null;
      if (dismiss) {
        dismissRegistry.delete(toastId);
        dismiss();
      }
      return;
    }

    const copyBtn = event.target.closest('.app-toast__copy');
    if (copyBtn) {
      event.preventDefault();
      event.stopPropagation();
      const message = copyBtn.closest('.app-toast')?.dataset?.toastMessage ?? '';
      copyToClipboard(message);
      copyBtn.classList.add('app-toast__copy--copied');
      window.setTimeout(() => copyBtn.classList.remove('app-toast__copy--copied'), 1500);
    }
  });
}

function normalizeOptions(options) {
  if (typeof options === 'string') {
    return { message: options };
  }
  return { ...options };
}

function createAppToastApi(globalProps = {}) {
  installClickHandler();

  const base = useToastBase({
    duration: 0,
    dismissible: false,
    pauseOnHover: false,
    queue: false,
    ...globalProps,
  });

  const open = (options) => {
    const opts = normalizeOptions(options);
    const plainMessage = opts.message ?? '';
    const toastId = String(++toastIdCounter);

    const instance = base.open({
      ...opts,
      duration: 0,
      dismissible: false,
      pauseOnHover: false,
      queue: false,
      message: wrapToastMessage(plainMessage, toastId),
    });

    dismissRegistry.set(toastId, instance.dismiss);

    return instance;
  };

  return {
    open,
    clear: base.clear,
    success(message, options = {}) {
      return open({ ...options, message, type: 'success' });
    },
    error(message, options = {}) {
      return open({ ...options, message, type: 'error' });
    },
    info(message, options = {}) {
      return open({ ...options, message, type: 'info' });
    },
    warning(message, options = {}) {
      return open({ ...options, message, type: 'warning' });
    },
    default(message, options = {}) {
      return open({ ...options, message, type: 'default' });
    },
  };
}

export function useAppToast(globalProps = {}) {
  return createAppToastApi(globalProps);
}

export default createAppToastApi;
